import React, { useState, useEffect, useCallback } from 'react';
import { useIconCache } from '@/hooks/useIconCache';
import { iconManager } from '@/utils/IconManager';

interface SupabaseIconProps {
  iconPath: string;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;
  className?: string;
  fallback?: string;
  enableFallback?: boolean;
  enableCache?: boolean;
  onLoad?: () => void;
  onError?: (error: string) => void;
  onClick?: () => void;
  style?: React.CSSProperties;
}

/**
 * Enhanced Supabase icon component with caching, fallbacks, and error handling
 */
export const SupabaseIcon: React.FC<SupabaseIconProps> = ({
  iconPath,
  alt,
  size = 'md',
  className = '',
  fallback,
  enableFallback = true,
  enableCache = true,
  onLoad,
  onError,
  onClick,
  style
}) => {
  const [currentSrc, setCurrentSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const { getCachedIcon, isInCache } = useIconCache();

  // Size mappings
  const sizeMap = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 48,
    '2xl': 64
  };

  const iconSize = typeof size === 'number' ? size : sizeMap[size];

  // Load icon with caching and fallback
  const loadIcon = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let iconUrl: string;

      if (enableCache) {
        iconUrl = await getCachedIcon(iconPath);
      } else {
        iconUrl = await iconManager.getIconUrl(iconPath, enableFallback);
      }

      setCurrentSrc(iconUrl);
      setIsLoading(false);
      onLoad?.();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load icon';
      setError(errorMessage);
      setIsLoading(false);
      onError?.(errorMessage);

      // Try fallback if enabled and not already tried
      if (enableFallback && retryCount === 0 && !fallback) {
        setRetryCount(1);
        // Try with default fallback
        setTimeout(() => loadIcon(), 100);
      } else if (fallback) {
        // Use custom fallback
        try {
          const fallbackUrl = enableCache 
            ? await getCachedIcon(fallback)
            : await iconManager.getIconUrl(fallback, false);
          setCurrentSrc(fallbackUrl);
          setError(null);
        } catch {
          // Fallback also failed
          console.error(`Both icon and fallback failed: ${iconPath} -> ${fallback}`);
        }
      }
    }
  }, [iconPath, fallback, enableFallback, enableCache, getCachedIcon, onLoad, onError, retryCount]);

  // Load icon on mount or when iconPath changes
  useEffect(() => {
    setRetryCount(0);
    loadIcon();
  }, [iconPath, loadIcon]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    if (retryCount < 2 && !error) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => loadIcon(), 200 * retryCount); // Exponential backoff
    }
  }, [retryCount, error, loadIcon]);

  // Base classes
  const baseClasses = [
    'inline-block',
    'object-contain',
    'transition-opacity',
    'duration-200'
  ].join(' ');

  // Loading state
  if (isLoading) {
    return (
      <div 
        className={`${baseClasses} bg-gray-200 animate-pulse rounded ${className}`}
        style={{ 
          width: iconSize, 
          height: iconSize,
          ...style 
        }}
        aria-label={alt || 'Loading icon'}
      >
        <div className="w-full h-full bg-gray-300 rounded"></div>
      </div>
    );
  }

  // Error state
  if (error && !currentSrc) {
    return (
      <div 
        className={`${baseClasses} bg-red-100 border border-red-200 rounded flex items-center justify-center text-red-600 text-xs font-medium ${className}`}
        style={{ 
          width: iconSize, 
          height: iconSize,
          ...style 
        }}
        title={`Failed to load icon: ${error}`}
        aria-label={alt || 'Icon failed to load'}
      >
        ?
      </div>
    );
  }

  // Successfully loaded icon
  return (
    <img
      src={currentSrc}
      alt={alt || iconPath.split('/').pop()?.replace(/\.[^/.]+$/, '') || 'icon'}
      className={`${baseClasses} ${className}`}
      style={{ 
        width: iconSize, 
        height: iconSize,
        ...style 
      }}
      onError={handleImageError}
      onClick={onClick}
      loading="lazy"
      decoding="async"
    />
  );
};

export default SupabaseIcon;